import { Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { PaymentTransaction } from '../../../../models/payment-transaction';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { Params } from '../../../../services/utilis/add-params-service';
import { enumToOptions } from '../../../../@shared/utility/enumToOptions';
import { CustomerService } from '../../../../services/customers/customer-service';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { PaymentTransactionType } from '../../../../models/enums';

@Component({
  selector: 'app-customer-payment-transaction-history-list',
  imports: [TableComponent],
  templateUrl: './customer-payment-transaction-history-list.html',
  styleUrl: './customer-payment-transaction-history-list.scss'
})
export class CustomerPaymentTransactionHistoryList implements OnInit {

  @ViewChild(TableComponent) table!: TableComponent<PaymentTransaction>;
  @Input() customerID: string | undefined

  params = [
    "page",
    "size",
    'sortBy',
    'sortDirection',
    'transactionType',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    transactionType: "transactionType",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  tableColumns: DynamicColumn<PaymentTransaction>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    {
      header: 'Transaction type',
      field: 'transactionType',
      sortable: true,
      isCapitalize: true,
      isFilterable: true,
      filterType: 'select',
      filterOptions: enumToOptions(PaymentTransactionType),
    },
    {
      header: 'Ride ID',
      field: 'rideId',
      sortable: true,
    },
    {
      header: 'Name',
      field: 'firstName',
      mergedName: 'lastName',
      sortable: false,
    },
    {
      header: 'Amount',
      field: 'amount',
      suffixText: "AED",
      sortable: true,
    },
    { header: 'Checkout ID', field: 'checkoutId', sortable: true },
    { header: 'Payment ID', field: 'paymentId', sortable: true },
    { header: 'Checkout Status', field: 'checkoutStatus', sortable: true, isCapitalize: true, },
    { header: 'Payment Status', field: 'paymentStatus', sortable: true, isCapitalize: true, },
    { header: 'Card Brand', field: 'cardBrand', sortable: false, visible: false },
    { header: 'Card Last 4 Digits', field: 'cardLast4', sortable: false, visible: false },
    {
      header: 'Three DS Required',
      field: 'threeDSRequired',
      sortable: true,
      isFilterable: true,
      isBoolean: true,
      trueLabel: 'YES',
      falseLabel: 'NO',
    },
    {
      header: 'Three DS Completed',
      field: 'threeDSCompleted',
      sortable: true,
      isFilterable: true,
      isBoolean: true,
      trueLabel: 'YES',
      falseLabel: 'NO',
    },
    {
      header: 'Verification Refunded',
      field: 'verificationRefunded',
      sortable: true,
      isFilterable: true,
      isBoolean: true,
      trueLabel: 'YES',
      falseLabel: 'NO',
    },
    {
      header: 'Created at',
      field: 'createdAt',
      sortable: true,
      isDateTime: true,
    },
  ];

  paymentTransactionsList = signal<PaymentTransaction[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  private lastParams = new BehaviorSubject<Record<string, unknown> | null>(null);

  private customerService = inject(CustomerService);
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getPaymentTransactionsList();
  }

  getPaymentTransactionsList = () => {
    this.activatedRoute.queryParams.pipe(
      debounceTime(0),
      filter(params => params['tab'] === '4'),
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      filter((queryParams) => {
        // 🔥 only continue if params changed
        if (JSON.stringify(this.lastParams.value) === JSON.stringify(queryParams)) {
          return false;
        }
        this.lastParams.next(queryParams);
        return true;
      }),

      tap((queryParams) => {
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),

      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'createdAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.customerService.getCustomerPaymentTransactionHistory(this.customerID!, params);
      })
    )
      .subscribe((res) => {
        this.paymentTransactionsList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  resetAll = () => {
    this.table?.clearAllFilters();
  }
}
